import { useState } from "react";
import { BsTruck } from "react-icons/bs";
import { ChevronRightIcon, PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  ICartDetailsResponseData,
  ICartHdCartItem,
  IHDCartAddressListResponseData,
} from "@/api_framework/api_modals/Cart";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import CartDetailsModal from "@/app/components/routes/auth_routes/CartManagement/CartDetailsModal";

interface CartPriceBreakupPanelProps {
  activeCartDetails: ICartDetailsResponseData | null;
  customerName: string;
  availableHdItems: ICartHdCartItem[];
  selectedDeliveryAddresses: IHDCartAddressListResponseData[] | undefined;
  addressList: IHDCartAddressListResponseData[] | undefined;
  onChangeAddress: () => void;
  onAddAddress: () => void;
  onViewDetails: () => void;
  isCartTabeLoading: boolean;
  isHDCartTabeLoading: boolean;
  checkOutLoader: boolean;
  finalCheckoutDisabled: boolean;
  isBillable: boolean | undefined;
  isParavetMissing: boolean;
  onCheckout: () => void;
}

const addressLabelClass = (label: string) =>
  `${label === "Home" ? "bg-[#e7f8ef] text-[#07863d]" : ""} ${label === "Office" ? "bg-[#f8e7d6] text-[#a75610]" : ""} ${label === "Others" ? "bg-[#eff1ff] text-[#3969ff]" : ""}`;

const CartPriceBreakupPanel = ({
  activeCartDetails,
  customerName,
  availableHdItems,
  selectedDeliveryAddresses,
  addressList,
  onChangeAddress,
  onAddAddress,
  onViewDetails,
  isCartTabeLoading,
  isHDCartTabeLoading,
  checkOutLoader,
  finalCheckoutDisabled,
  isBillable,
  isParavetMissing,
  onCheckout,
}: CartPriceBreakupPanelProps) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const priceBreakup = activeCartDetails?.price_breakup;
  const hasHdItems = availableHdItems?.length > 0;
  const hasDiscount = Number(priceBreakup?.total_discount) > 0;
  const deliveryAddress = selectedDeliveryAddresses?.[0];

  return (
    <div className="relative z-10 mx-2 mb-2 shrink-0 rounded-xl border border-[#b2ddff] bg-white px-5 py-3 shadow-[0_-6px_20px_-6px_rgba(16,24,40,0.15)] cart-print-price-card">
      <div className="flex items-center justify-between gap-6">
        <div className="min-w-0 flex-1">
          {hasHdItems && (
            <div className="flex min-w-0 max-w-md items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#101828] ring-1 ring-[#e3e8ef]">
                <BsTruck className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {deliveryAddress ? (
                    <>
                      <span
                        className={`shrink-0 rounded-2xl px-2 py-0.5 text-[10px] font-medium ${addressLabelClass(deliveryAddress?.label)}`}
                      >
                        {deliveryAddress?.label}
                      </span>
                      {deliveryAddress?.previously_ordered && (
                        <span className="shrink-0 rounded-md bg-[#dedede] px-1.5 py-0.5 text-[10px] text-[#666666] whitespace-nowrap">
                          Last Delivered
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs font-medium text-[#101828]">No delivery address selected</span>
                  )}
                  {addressList?.length > 0 ? (
                    <button
                      type="button"
                      onClick={onChangeAddress}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0069fe] hover:underline"
                    >
                      <PencilSquareIcon className="h-3 w-3" />
                      Change
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onAddAddress}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0069fe] hover:underline"
                    >
                      <PlusIcon className="h-3 w-3" />
                      Add Address
                    </button>
                  )}
                </div>
                {deliveryAddress && (
                  <p
                    className="mt-1 max-w-[260px] truncate text-xs font-medium text-[#101828]"
                    title={`${deliveryAddress?.address_1 ?? ""} ${deliveryAddress?.address_2 ?? ""}`}
                  >
                    {deliveryAddress?.address_1 ?? ""} {deliveryAddress?.address_2 ?? ""}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-5">
          <div className="flex flex-col items-end gap-1">
            {hasDiscount && (
              <span className="rounded-md bg-[#e7f8ef] px-2.5 py-1 text-sm font-bold text-[#07863d] whitespace-nowrap">
               Savings: ₹{priceBreakup?.total_discount}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                onViewDetails();
                setIsDetailsOpen(true);
              }}
              className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#0069fe] hover:underline cart-print-hide"
            >
              View Details
              <ChevronRightIcon className="h-3 w-3" />
            </button>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-xl font-bold leading-7 text-[#101828] tabular-nums whitespace-nowrap">
              ₹{priceBreakup?.net_total_after_tax}
            </span>
            {hasDiscount && (
              <span className="text-sm text-[#98a2b3] line-through tabular-nums">
                ₹{priceBreakup?.total_gross_price}
              </span>
            )}
          </div>
          {isCartTabeLoading || isHDCartTabeLoading || checkOutLoader ? (
            <div className="flex h-11 w-[140px] items-center justify-center">
              <CircleSpinnerLoader h="20" w="20" />
            </div>
          ) : (
            <button
              type="button"
              disabled={finalCheckoutDisabled || isBillable || isParavetMissing}
              title={isBillable ? "Ask the Doctor to close the EMR Billable first" : ""}
              onClick={onCheckout}
              className={`h-11 w-[140px] rounded-lg text-sm font-semibold text-white shadow-sm transition-colors ${
                finalCheckoutDisabled || isBillable || isParavetMissing
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#07863d] hover:bg-[#066e32] cursor-pointer"
              }`}
            >
              Checkout
            </button>
          )}
        </div>
      </div>
      {(isBillable || isParavetMissing) && (
        <p className="mt-2 ml-auto w-fit rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-600">
          {isBillable
            ? "Ask the Doctor to close the EMR Billable first"
            : "Assign paravet before checkout for this cart."}
        </p>
      )}
      <CartDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        customerName={customerName}
        activeCartDetails={activeCartDetails}
        hdItems={availableHdItems}
      />
    </div>
  );
};

export default CartPriceBreakupPanel;
